import React, { useState, useEffect, useRef } from 'react';
import Radio from 'antd/lib/radio';
import DatePicker from 'antd/lib/date-picker';
import Spin from 'antd/lib/spin';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Tooltip from 'antd/lib/tooltip';
import Menu from 'antd/lib/menu';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { CSVLink } from 'react-csv';
import { getDiagnosticsData, getDiagnosticsDataPoints, getCSVData } from 'parity-network-requests';
import * as util from '../../../../utils/urlParams';
import { MonitoringLineChart } from './components/MonitoringLineChart';
import { MonitoringModal } from './components/MonitoringModal';
import { config } from '../../../../config';

// ICONS
import dropdownArrowGrey from '../../../../icons/dropdown-grey.svg';
import dropdownArrowLightGrey from '../../../../icons/dropdown-light-grey.svg';
import dropdownArrowBlue from '../../../../icons/dropdown-blue.svg';
import editIcon from './components/assets/edit.svg';
import downloadIcon from './components/assets/download.svg';
import arrowLeft from './components/assets/Arrowleft.svg';
import arrowRight from './components/assets/Arrowright.svg';

import './MonitoringDetails.less';

const { REACT_APP_BUILD_ENV } = config;

export const MonitoringDetails = (props) => {
  const {
    category: {
      data_api_location: dataApiLocation,
      diagnostic_category: { slug, display_name: displayName },
    },
    building,
    selectedCategory: {
      api_location: apiLocation,
      diagnostic_category: { display_name: categoryDisplayName },
    },
    theme,
  } = props;
  const [view, setView] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    endTime: moment().utc().format(),
    startTime: moment().subtract(1, 'hour').utc().format(),
  });
  const [selectedCategory, setSelectedCategory] = useState('');
  const [calendarDateRange, setCalendarDateRange] = useState(['', '']);
  const [graphData, setGraphData] = useState({});
  const [categoryDataPoints, setCategorydataPoints] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [CSVData, setCSVData] = useState([]);
  const { search } = useLocation();
  const { start, end, data_points, period } = queryString.parse(search);
  const history = useHistory();
  const { RangePicker } = DatePicker;
  const csvLink = useRef(null);

  const fetchCSVData = async () => {
    const { startTime, endTime } = dateRange;
    const diff = moment(endTime).diff(moment(startTime), 'days', true);
    const interval = diff > 31 ? 'hour' : 'fifteen';
    const tags = Object.keys(JSON.parse(sessionStorage.getItem('lineColorObj')));

    const csvData = await getCSVData(
      REACT_APP_BUILD_ENV,
      building,
      slug,
      dateRange.startTime,
      dateRange.endTime,
      interval,
      tags,
    );

    if (csvData) {
      await setCSVData(csvData);

      csvLink.current.link.click();
    }
  };

  const setLineColorObj = (dataPoints) => {
    const customDataPoints = util.getDataPointsFromUrlParams(search);
    const tempColorObj = {};
    const colors = [
      'line0',
      'line1',
      'line2',
      'line3',
      'line4',
      'line5',
      'line6',
      'line7',
      'line8',
      'line9',
      'line10',
      'line11',
      'line12',
      'line13',
      'line14',
    ];

    if (!customDataPoints.length) {
      let defaultDataPoints = dataPoints.filter((dataPoint) => dataPoint.is_default === true);
      if (categoryDisplayName === 'All') {
        defaultDataPoints = dataPoints.filter((dataPoint) => dataPoint.tag === 'CLDOAT');
      }
      if (!defaultDataPoints.length) {
        defaultDataPoints = dataPoints;
      }
      defaultDataPoints.map((dataPoint) => {
        tempColorObj[dataPoint.tag] = colors.shift();
        return tempColorObj;
      });
    } else {
      customDataPoints.map((dataPoint) => {
        tempColorObj[dataPoint] = colors.shift();
        return tempColorObj;
      });
    }

    sessionStorage.setItem('lineColorObj', JSON.stringify(tempColorObj));
  };

  useEffect(() => {
    async function fetchGraphData() {
      setView('1 hour');
      if (period) {
        setView(period.replace('_', ' '));
      }
      const { startTime, endTime } = dateRange;

      const clearData = sessionStorage.getItem('clearData');
      if (clearData === 'true') {
        setLineColorObj([]);
        setGraphData([]);
      } else {
        let customDataPointsFromUrl = data_points ? data_points.split(',') : [];
        if (!customDataPointsFromUrl.length && categoryDisplayName === 'All') {
          customDataPointsFromUrl = ['CLDOAT'];
        }
        const customData =
          customDataPointsFromUrl.length > 0
            ? customDataPointsFromUrl
            : JSON.parse(sessionStorage.getItem('customDataPoints')) || [];

        if (start && end) {
          const monitoringGraphData = await getDiagnosticsData(
            REACT_APP_BUILD_ENV,
            dataApiLocation,
            customData,
            start,
            end,
          );
          setGraphData(monitoringGraphData);
        } else {
          const monitoringGraphData = await getDiagnosticsData(
            REACT_APP_BUILD_ENV,
            dataApiLocation,
            customData,
            startTime,
            endTime,
          );
          setLineColorObj(monitoringGraphData.data_points);
          setGraphData(monitoringGraphData);
          setView('1 hour');
        }

        const monitoringDataPoints = await getDiagnosticsDataPoints(REACT_APP_BUILD_ENV, apiLocation);

        setLineColorObj(monitoringDataPoints.data_points);
        setSelectedCategory(categoryDisplayName);
        setCategorydataPoints(monitoringDataPoints.data_points);
        if (categoryDisplayName === 'All') {
          const defaultDataPoints = monitoringDataPoints.data_points.filter((dataPoint) => dataPoint.tag === 'CLDOAT');
          if (defaultDataPoints.length === 0 && !customDataPointsFromUrl.length) {
            setGraphData([]);
          }
        }
      }

      setIsLoading(false);
    }
    fetchGraphData();
  }, [displayName, dataApiLocation, apiLocation, view, dateRange, data_points]);

  const handleViewChange = (selectedView) => {
    setView(selectedView);

    const endTime = moment().utc().format();
    let startTime;

    switch (selectedView) {
      case '1 hour':
        startTime = moment(endTime).subtract(1, 'hour').utc().format();
        break;
      case '6 hours':
        startTime = moment(endTime).subtract(6, 'hours').utc().format();
        break;
      case '24 hours':
        startTime = moment(endTime).subtract(24, 'hours').utc().format();
        break;
      case '7 days':
        startTime = moment(endTime).subtract(7, 'days').utc().format();
        break;
      case '30 days':
        startTime = moment(endTime).subtract(30, 'days').utc().format();
        break;
      default:
        startTime = moment(endTime).subtract(1, 'hour').utc().format();
        break;
    }

    setDateRange({
      endTime,
      startTime,
    });

    const selectedPeriod = selectedView.replace(' ', '_');

    history.replace(
      `/monitoring/${building}/?category=${displayName}&period=${selectedPeriod}&start=${startTime}&end=${endTime}&data_points=${
        data_points || ''
      }`,
    );

    setCalendarDateRange(['', '']);
  };

  const handleCalendarChange = (dates, dateStrings) => {
    if (dateStrings[0] && dateStrings[1]) {
      const startTime = dateStrings[0];
      const endTime = dateStrings[1];

      if (startTime === endTime) {
        // handle the case where they select a single day
        setDateRange({
          startTime: moment(startTime).startOf('day').utc().format(),
          endTime: moment(endTime).endOf('day').utc().format(),
        });

        history.replace(
          `/monitoring/${building}/?category=${displayName}&start=${moment(startTime)
            .startOf('day')
            .utc()
            .format()}&end=${moment(endTime).endOf('day').utc().format()}&data_points=${data_points || ''}`,
        );
      } else {
        setDateRange({
          startTime: moment(dateStrings[0]).utc().format(),
          endTime: moment(dateStrings[1]).utc().format(),
        });

        history.replace(
          `/monitoring/${building}/?category=${displayName}&start=${moment(dateStrings[0]).utc().format()}&end=${moment(
            dateStrings[1],
          )
            .utc()
            .format()}&data_points=${data_points || ''}`,
        );
      }

      setCalendarDateRange([moment(startTime), moment(endTime)]);
      setView('');
    }
  };

  const scrollView = (direction) => {
    const { startTime, endTime } = dateRange;
    let newStartTime;
    let newEndTime;
    let timeDiff;

    if (direction === 'back') {
      switch (view) {
        case '1 hour':
          newStartTime = moment(startTime).subtract(1, 'hour').utc().format();
          newEndTime = moment(endTime).subtract(1, 'hour').utc().format();
          break;
        case '6 hours':
          newStartTime = moment(startTime).subtract(6, 'hours').utc().format();
          newEndTime = moment(endTime).subtract(6, 'hours').utc().format();
          break;
        case '24 hours':
          newStartTime = moment(startTime).subtract(24, 'hours').utc().format();
          newEndTime = moment(endTime).subtract(24, 'hours').utc().format();
          break;
        case '7 days':
          newStartTime = moment(startTime).subtract(7, 'days').utc().format();
          newEndTime = moment(endTime).subtract(7, 'days').utc().format();
          break;
        case '30 days':
          newStartTime = moment(startTime).subtract(30, 'days').utc().format();
          newEndTime = moment(endTime).subtract(30, 'days').utc().format();
          break;
        default:
          timeDiff = calendarDateRange[1].diff(calendarDateRange[0], 'days');
          newStartTime = moment(startTime).subtract(timeDiff, 'days').utc().format();
          newEndTime = moment(endTime).subtract(timeDiff, 'days').utc().format();
          break;
      }
    } else {
      switch (view) {
        case '1 hour':
          newStartTime = moment(startTime).add(1, 'hour').utc().format();
          newEndTime = moment(endTime).add(1, 'hour').utc().format();
          break;
        case '6 hours':
          newStartTime = moment(startTime).add(6, 'hours').utc().format();
          newEndTime = moment(endTime).add(6, 'hours').utc().format();
          break;
        case '24 hours':
          newStartTime = moment(startTime).add(24, 'hours').utc().format();
          newEndTime = moment(endTime).add(24, 'hours').utc().format();
          break;
        case '7 days':
          newStartTime = moment(startTime).add(7, 'days').utc().format();
          newEndTime = moment(endTime).add(7, 'days').utc().format();
          break;
        case '30 days':
          newStartTime = moment(startTime).add(30, 'days').utc().format();
          newEndTime = moment(endTime).add(30, 'days').utc().format();
          break;
        default:
          timeDiff = calendarDateRange[1].diff(calendarDateRange[0], 'days');
          newStartTime = moment(startTime).subtract(timeDiff, 'days').utc().format();
          newEndTime = moment(endTime).subtract(timeDiff, 'days').utc().format();
          break;
      }
    }
    history.replace(
      `/monitoring/${building}/?category=${displayName}&start=${newStartTime}&end=${newEndTime}&data_points=${
        data_points || ''
      }`,
    );
    setDateRange({ endTime: newEndTime, startTime: newStartTime });
  };

  const disableScroll = () => {
    const { endTime } = dateRange;
    const endTimeHour = moment(endTime).utc().format('dddd, MMMM Do YYYY, h');
    const requestedHour = moment().utc().format('dddd, MMMM Do YYYY, h');
    return endTimeHour === requestedHour;
  };

  const disabledDates = (current) => {
    return current && current > moment();
  };

  const onModalClose = () => {
    setIsModalOpen(false);
  };

  const saveChanges = async (dataPoints) => {
    const { startTime, endTime } = dateRange;
    const tempColorObj = {};
    const colors = [
      'line0',
      'line1',
      'line2',
      'line3',
      'line4',
      'line5',
      'line6',
      'line7',
      'line8',
      'line9',
      'line10',
      'line11',
      'line12',
      'line13',
      'line14',
    ];

    dataPoints.map((dataPoint) => {
      tempColorObj[dataPoint] = colors.shift();
      return tempColorObj;
    });

    sessionStorage.setItem('lineColorObj', JSON.stringify(tempColorObj));
    sessionStorage.setItem('customDataPoints', JSON.stringify(dataPoints));

    setIsModalOpen(false);

    const dataPointParams = util.addSearchParams(dataPoints);
    history.replace(
      `/monitoring/${building}/?category=${displayName}&start=${startTime}&end=${endTime}${dataPointParams}`,
    );

    const clearData = sessionStorage.getItem('clearData');
    if (clearData === 'true') {
      setGraphData([]);
    }

    if (dataPoints.length) {
      const monitoringGraphData = await getDiagnosticsData(
        REACT_APP_BUILD_ENV,
        dataApiLocation,
        dataPoints,
        startTime,
        endTime,
      );
      setGraphData(monitoringGraphData);
    }
  };

  const renderLegendItems = () => {
    const customData = JSON.parse(sessionStorage.getItem('customDataPoints')) || [];
    let lineColorObj = JSON.parse(sessionStorage.getItem('lineColorObj'));
    let customDataPoints = util.getDataPointsFromUrlParams(search);
    const clearData = sessionStorage.getItem('clearData');
    if (clearData === 'true') {
      lineColorObj = {};
      return <div className="monitoring-details-legend__item" />;
    }

    if (customDataPoints && customDataPoints.length > 0) {
      sessionStorage.setItem('customDataPoints', JSON.stringify(customDataPoints));
      customDataPoints = categoryDataPoints.filter((dataPoint) => customDataPoints.includes(dataPoint.tag));

      return customDataPoints.map((dataPoint) => {
        const { status, modbus_register: modbusRegister, display_name: legendDisplayName, tag } = dataPoint;
        const lineStyle = `line ${lineColorObj[tag]}`;

        return (
          <div className="monitoring-details-legend__item" key={tag}>
            <div className="line-width">
              <div className={lineStyle} />
            </div>
            <div className="wide-column">{legendDisplayName}</div>
            <div className="data-tag-width">{tag}</div>
            <div className="equal-width desktop">{modbusRegister}</div>
            <div className="equal-width">{status}</div>
          </div>
        );
      });
    }

    if (customData.length > 0) {
      customDataPoints = categoryDataPoints.filter((dataPoint) => customData.includes(dataPoint.tag));

      return customDataPoints.map((dataPoint) => {
        const { status, modbus_register: modbusRegister, display_name: legendDisplayName, tag } = dataPoint;
        const lineStyle = `line ${lineColorObj[tag]}`;

        return (
          <div className="monitoring-details-legend__item" key={tag}>
            <div className="line-width">
              <div className={lineStyle} />
            </div>
            <div className="wide-column">{legendDisplayName}</div>
            <div className="data-tag-width">{tag}</div>
            <div className="equal-width desktop">{modbusRegister}</div>
            <div className="equal-width">{status}</div>
          </div>
        );
      });
    }

    let defaultDataPoints = categoryDataPoints.filter((dataPoint) => dataPoint.is_default === true);
    if (selectedCategory === 'All') {
      defaultDataPoints = categoryDataPoints.filter((dataPoint) => dataPoint.tag === 'CLDOAT');
    }

    return defaultDataPoints.map((dataPoint, index) => {
      const { status, modbus_register: modbusRegister, display_name: legendDisplayName, tag } = dataPoint;
      const lineStyle = `line line${index}`;

      return (
        <div className="monitoring-details-legend__item" key={tag}>
          <div className="line-width">
            <div className={lineStyle} />
          </div>
          <div className="wide-column">{legendDisplayName}</div>
          <div className="data-tag-width">{tag}</div>
          <div className="equal-width desktop">{modbusRegister}</div>
          <div className="equal-width">{status}</div>
        </div>
      );
    });
  };

  const tooltipText = 'Please note that when you download this data, it is only available in 1 hour intervals.';

  const getDropdownIcon = () => {
    if (isDropdownOpen) {
      return <img className="dropdown-icon" src={dropdownArrowBlue} alt="dropdown" />;
    }
    return theme === 'light' ? (
      <img className="dropdown-icon" src={dropdownArrowGrey} alt="dropdown" />
    ) : (
      <img className="dropdown-icon" src={dropdownArrowLightGrey} alt="dropdown" />
    );
  };

  return (
    <div className={`monitoring-details monitoring-details-${theme}`}>
      <div className="monitoring-details-header">
        <div className="monitoring-details-header--text">View:</div>
        <Radio.Group
          value={view}
          onChange={(e) => handleViewChange(e.target.value)}
          className="monitoring-details-header--view"
        >
          <Radio.Button value="1 hour">1 Hour</Radio.Button>
          <Radio.Button value="6 hours">6 Hours</Radio.Button>
          <Radio.Button value="24 hours">24 Hours</Radio.Button>
          <Radio.Button value="7 days">7 Days</Radio.Button>
          <Radio.Button value="30 days">30 Days</Radio.Button>
        </Radio.Group>
        <Dropdown
          className={`monitoring-details-header--view-mobile app-dropdown app-dropdown-${theme}`}
          trigger={['click']}
          onVisibleChange={() => setIsDropdownOpen(!isDropdownOpen)}
          overlay={
            <Menu className={`app-dropdown-menu app-dropdown-menu-${theme}`}>
              <Menu.Item key="1 hour" onClick={(e) => handleViewChange(e.key)}>
                1 Hour
              </Menu.Item>
              <Menu.Item key="6 hours" onClick={(e) => handleViewChange(e.key)}>
                6 Hours
              </Menu.Item>
              <Menu.Item key="24 hours" onClick={(e) => handleViewChange(e.key)}>
                24 Hours
              </Menu.Item>
              <Menu.Item key="7 days" onClick={(e) => handleViewChange(e.key)}>
                7 Days
              </Menu.Item>
              <Menu.Item key="30 days" onClick={(e) => handleViewChange(e.key)}>
                30 Days
              </Menu.Item>
            </Menu>
          }
        >
          <div className="monitoring-details-header--view-mobile-select">
            {view}
            {getDropdownIcon()}
          </div>
        </Dropdown>
        <div className="monitoring-details-header--text">View Date Range:</div>
        <div className="monitoring-details-header--datepicker">
          <RangePicker
            value={calendarDateRange}
            onCalendarChange={(dates, dateStrings) => handleCalendarChange(dates, dateStrings)}
            disabledDate={disabledDates}
          />
        </div>
      </div>
      <div className="monitoring-details-row">
        <div className="monitoring-details-card-wrapper">
          <div className="monitoring-details-card">
            {isLoading ? (
              <div className="monitoring-details--loading">
                <Spin size="large" />
              </div>
            ) : (
              <>
                <div className="monitoring-details-card--header">
                  <div className="monitoring-details-card--title">
                    {start && end
                      ? `${moment(start).format('MMM DD, YYYY')} – ${moment(end).format('MMM DD, YYYY')}`
                      : `${moment(dateRange.startTime).format('MMM DD, YYYY')} – ${moment(dateRange.endTime).format(
                          'MMM DD, YYYY',
                        )}`}
                  </div>
                  <div className="monitoring-details-download">
                    <Tooltip overlayClassName={`app-tooltip-${theme}`} placement="bottom" title={tooltipText}>
                      <div
                        className="monitoring-details-card--download-button"
                        role="presentation"
                        onClick={() => fetchCSVData()}
                      >
                        Download Data
                        <img className="download-img" src={downloadIcon} alt="download-icon" />
                      </div>
                      <div
                        className="monitoring-details-card--download-button-mobile"
                        role="presentation"
                        onClick={() => fetchCSVData()}
                      >
                        <img className="download-img-mobile" src={downloadIcon} alt="download-icon" />
                      </div>
                    </Tooltip>

                    <CSVLink data={CSVData} filename="data.csv" className="hidden" ref={csvLink} target="_blank" />
                  </div>
                </div>
                <div className="monitoring-details--body">
                  <Button className="monitoring-details--scroll-button-desktop" onClick={() => scrollView('back')}>
                    <img src={arrowLeft} alt="scroll-left-button" />
                  </Button>
                  <MonitoringLineChart
                    data={graphData}
                    dateRange={dateRange}
                    selectedCategory={selectedCategory}
                    theme={theme}
                  />
                  <Button
                    className="monitoring-details--scroll-button-desktop"
                    onClick={() => scrollView('forward')}
                    disabled={disableScroll()}
                  >
                    <img src={arrowRight} alt="scroll-right-button" />
                  </Button>
                  <div className="monitoring-details--scroll-buttons-mobile">
                    <Button className="monitoring-details--scroll-button-mobile" onClick={() => scrollView('back')}>
                      <img src={arrowLeft} alt="scroll-left-button" />
                    </Button>
                    <Button
                      className="monitoring-details--scroll-button-mobile"
                      onClick={() => scrollView('forward')}
                      disabled={disableScroll()}
                    >
                      <img src={arrowRight} alt="scroll-right-button" />
                    </Button>
                  </div>
                </div>
                <div className="monitoring-details-card--legend">
                  <div className="monitoring-details-card--header">
                    <div className="monitoring-details-card--title">Legend</div>
                    <div className="monitoring-details-card__button-wrapper">
                      <Button
                        block
                        size="medium"
                        className="monitoring-details-card__button"
                        onClick={() => setIsModalOpen(true)}
                      >
                        <div className="monitoring-details-card__buton-text">
                          <img className="edit-img" src={editIcon} alt="edit-icon" />
                          Add / Remove Lines
                        </div>
                      </Button>
                    </div>
                  </div>
                  <div className="monitoring-details-legend__column-names">
                    <div className="line-width">Line</div>
                    <div className="wide-column">Data Set</div>
                    <div className="data-tag-width">Data Tag</div>
                    <div className="equal-width desktop">Modbus Register</div>
                    <div className="equal-width">Latest Reading</div>
                  </div>
                  {renderLegendItems()}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {isModalOpen && (
        <MonitoringModal
          categoryDataPoints={categoryDataPoints}
          selectedCategory={selectedCategory}
          onCancel={onModalClose}
          saveChanges={saveChanges}
          theme={theme}
        />
      )}
    </div>
  );
};

MonitoringDetails.propTypes = {
  category: PropTypes.shape({
    api_location: PropTypes.string,
    building: PropTypes.string,
    data_api_location: PropTypes.string,
    diagnostic_category: PropTypes.shape({
      display_name: PropTypes.string,
      slug: PropTypes.string,
    }),
    display_name: PropTypes.string,
    id: PropTypes.number,
  }).isRequired,
};
